import React from 'react';
import { parsePassageSegment, formatPassageLabel } from '../utils/passagePlans';

// Comma-separated passage input for Passages Plans, e.g.
// "Ephesians 2:8-10, Romans 3:23, John 3:16". Mirrors SuperAdmin's
// SmartChapterInput pill pattern, but validates against the "Book C:V(-V2)"
// passage syntax instead of whole-chapter "Book C(-C2)" syntax.
function PassageInput({ value, onChange, placeholder, id, label }) {
  // Pills for every segment closed off with a comma — the in-progress
  // segment being typed (no trailing comma yet) stays as plain text so a
  // pill doesn't pop up mid-keystroke.
  const trailingComma = /,\s*$/.test(value);
  const allParts = value.split(',');
  const inProgress = trailingComma ? '' : (allParts[allParts.length - 1] || '').trim();
  const confirmedSegments = (trailingComma ? allParts : allParts.slice(0, -1))
    .map(s => s.trim())
    .filter(Boolean);

  const removePill = (indexToRemove) => {
    const remaining = confirmedSegments.filter((_, i) => i !== indexToRemove);
    const newValue = inProgress
      ? [...remaining, inProgress].join(', ')
      : remaining.join(', ') + (remaining.length ? ', ' : '');
    onChange(newValue);
  };

  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">
          {label}
        </label>
      )}
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="input-field"
        placeholder={placeholder || 'e.g., Ephesians 2:8-10, Romans 3:23, John 3:16'}
      />

      {confirmedSegments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {confirmedSegments.map((segment, index) => {
            const parsed = parsePassageSegment(segment);
            return (
              <span
                key={`${segment}-${index}`}
                className={`inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-sm font-medium ${
                  parsed ? 'bg-green-100 dark:bg-green-400/15 text-green-800 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-400/15 text-amber-800 dark:text-amber-400'
                }`}
                title={parsed ? undefined : 'Not recognized — use "Book Chapter:Verse" e.g. "John 3:16"'}
              >
                {parsed ? (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.598c.75 1.334-.213 2.98-1.742 2.98H3.48c-1.53 0-2.493-1.646-1.743-2.98L8.257 3.1zM11 14a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                {parsed ? formatPassageLabel(parsed) : segment}
                <button
                  type="button"
                  onClick={() => removePill(index)}
                  className={`ml-0.5 rounded-full p-0.5 hover:bg-black/10 ${parsed ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}
                  title={`Remove "${segment}"`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      )}

      <p className="mt-2 text-xs text-gray-500 dark:text-white/50">
        <strong>Format:</strong> "Book Chapter:Verse" for a single verse (e.g. "John 3:16"), or
        "Book Chapter:Verse-Verse" for a range (e.g. "Ephesians 2:8-10"). Separate with commas.
      </p>
    </div>
  );
}

export default PassageInput;

// Parses the full comma-separated value into an ordered array of passage
// objects, dropping anything that doesn't validate. Used by callers (e.g.
// PassagesPlansPage) when actually submitting the plan.
export function parsePassageInputValue(value) {
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(parsePassageSegment)
    .filter(Boolean);
}
